import type { ListBuilderConfiguration } from '../types';

/**
 * License Files Embedded list configuration
 * Shows files attached to a license
 * Converted from JSON to TypeScript for type safety
 */
export const LICENSE_FILES_EMBEDDED_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-license-files-embedded',
			cacheTTL: 300000,
			name: 'Files',
			type: 'default',
			active: true,
			sortOrder: 0,
			columns: [
				{
					id: 'name-column',
					fields: [
						{
							id: 'file-name',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'fileName', objectTable: 'license_file' },
							},
							link: {
								type: 'callback',
								enabled: true,
								callbackName: 'view',
							},
							styleConfig: {
								size: 'sm',
								weight: 'medium',
								colorRule: { type: 'theme', themeColor: 'statusInfo' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h-name', text: 'Name' }],
					sortable: true,
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'fileName',
							objectTable: 'license_file',
						},
					],
				},
				{
					id: 'type-column',
					fields: [
						{
							id: 'file-type',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'fileType', objectTable: 'license_file' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h-type', text: 'Type' }],
					width: 150,
					sortable: true,
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'fileType',
							objectTable: 'license_file',
						},
					],
				},
				{
					id: 'source-column',
					fields: [
						{
							id: 'file-source',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'source', objectTable: 'license_file' },
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'value-mapping' },
								valueMapping: [
									{
										value: 'user',
										displayText: 'User',
									},
									{
										value: 'registry',
										displayText: 'Registry',
									},
								],
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h-source', text: 'Source' }],
					width: 110,
					sortable: true,
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'source',
							objectTable: 'license_file',
						},
					],
				},
				{
					id: 'date-column',
					fields: [
						{
							id: 'upload-date',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: '_created_date',
									objectTable: 'license_file',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
								formatOptions: { dateFormat: 'date-medium' },
							},
							displayStyle: 'date',
						},
					],
					headers: [{ id: 'h-date', text: 'Uploaded' }],
					width: 130,
					sortable: true,
					sortFields: [
						{
							direction: 'DESC',
							fieldName: '_created_date',
							objectTable: 'license_file',
						},
					],
				},
				{
					id: 'uploader-column',
					fields: [
						{
							id: 'uploader-name',
							dataConfig: {
								type: 'template',
								template: '{uploadedByFirstName} {uploadedByLastName}',
								templateFields: [],
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h-uploader', text: 'Uploaded By' }],
					width: 140,
					sortable: true,
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'uploadedByFirstName',
							objectTable: 'license_file',
						},
					],
				},
				{
					id: 'actions-column',
					fields: [
						{
							id: 'actions-dropdown',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'actions_dropdown',
							dropdownActions: [
								{
									id: 'view-action',
									label: 'View',
									iconName: 'eye',
									callbackName: 'view',
									variant: 'secondary',
									visibility: 'visible',
								},
								{
									id: 'download-action',
									label: 'Download',
									iconName: 'download',
									callbackName: 'download',
									variant: 'secondary',
									visibility: 'overflow',
								},
								{
									id: 'delete-action',
									label: 'Delete',
									iconName: 'trash',
									callbackName: 'delete',
									danger: true,
									visibility: 'overflow',
									visibilityCallback: 'canDeleteFile',
									requiredPermission: 'licenses:update',
								},
							],
						},
					],
					headers: [{ id: 'h-actions', text: '' }],
					width: 80,
					align: 'center',
					sortable: false,
				},
			],
			defaultSortId: 'column-date-column-desc',
		},
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [10, 25, 50, 100],
	},
	filters: {
		activeFilters: [],
		defaultFilters: {},
	},
	objects: [
		{
			objectLabel: 'License File',
			objectTable: 'license_file',
			selectedFields: [
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: 'fk_license__id', fieldLabel: 'License ID' },
				{ fieldName: 'fk_file__id', fieldLabel: 'File ID' },
				{ fieldName: 'fileName', fieldLabel: 'File Name' },
				{ fieldName: 'fileType', fieldLabel: 'File Type' },
				{ fieldName: 'fileSize', fieldLabel: 'File Size' },
				{ fieldName: '_created_date', fieldLabel: 'Upload Date' },
				{ fieldName: 'uploadedByFirstName', fieldLabel: 'Uploader First Name' },
				{ fieldName: 'uploadedByLastName', fieldLabel: 'Uploader Last Name' },
				{ fieldName: 'source', fieldLabel: 'Source' },
				{ fieldName: 'registryFileKey', fieldLabel: 'Registry File Key' },
				{ fieldName: 'registryStateCode', fieldLabel: 'Registry State' },
			],
		},
	],
	tableSorts: [
		{
			id: 'date-desc',
			name: 'Upload Date (Newest First)',
			fields: [
				{
					fieldName: '_created_date',
					objectTable: 'license_file',
					direction: 'DESC',
				},
			],
		},
		{
			id: 'date-asc',
			name: 'Upload Date (Oldest First)',
			fields: [
				{
					fieldName: '_created_date',
					objectTable: 'license_file',
					direction: 'ASC',
				},
			],
		},
		{
			id: 'name-asc',
			name: 'File Name (A-Z)',
			fields: [
				{
					fieldName: 'fileName',
					objectTable: 'license_file',
					direction: 'ASC',
				},
			],
		},
		{
			id: 'name-desc',
			name: 'File Name (Z-A)',
			fields: [
				{
					fieldName: 'fileName',
					objectTable: 'license_file',
					direction: 'DESC',
				},
			],
		},
	],
};
